import { MButton } from '@assets/mui/@material-extend';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DELIVERY_FORM_CONSTANTS from '../../consts';
import { RouteWidgetParams } from '../../types';
import useDelivery from '../../useDelivery';
import { checkIsAlternativeVariantsChanged } from './helpers';

function VariantTooltip({ variantName }: { variantName: string }) {
  const { t } = useTranslation();
  return (
    <span style={{ marginRight: '3px' }}>
      <Tooltip
        placement="top"
        title={
          <div
            dangerouslySetInnerHTML={{
              __html: `<p><b>${t(
                'RoutesListComponent:AlternativeVariants.AlertDetailsCommon.Items.' +
                  variantName +
                  '.Name'
              )}</b> ${t(
                'RoutesListComponent:AlternativeVariants.AlertDetailsCommon.Items.' +
                  variantName +
                  '.Tooltip'
              )}</p>`,
            }}
            className="a-html-content"
          />
        }
      >
        <IconButton
          sx={{
            width: 16,
            height: 16,
            backgroundColor: '#E9FCD4',
            marginBottom: '2px',
          }}
        >
          <QuestionMarkIcon sx={{ fontSize: 8, color: '#707070' }} />
        </IconButton>
      </Tooltip>
    </span>
  );
}

// Компонент вывода параметров контейнерной перевозки для маршрута
const RouteParameters: FC<{}> = () => {
  const { t } = useTranslation();

  const { useIsRoutesLoading, useIsLoading, useRouteParams, useBaseDate } = useDelivery();
  const [isRoutesLoading] = useIsRoutesLoading().useState();
  const [isLoading] = useIsLoading().useState();
  const [baseData] = useBaseDate().useState();
  const [routeParams, setRouteParameter] = useRouteParams().useState();

  const [isExpanded, setIsExpanded] = useState<boolean>(() =>
    checkIsAlternativeVariantsChanged(routeParams, DELIVERY_FORM_CONSTANTS.routeParams)
  );

  const {
    isNeedContainerRent,
    isNeedPrekeridge,
    startLocation,
    endLocation,
    isWithoutPrekeridge,
    isWithoutOnkeridge,
    isFromTerminalRadius,
    isToTerminalRadius,
    isWithCrossDocking,
  } = routeParams;

  const startTransportingCondition = baseData?.startTransportingConditions.find(
    (c) => c.id === routeParams.startTransportingCondition
  );

  const endTransportingCondition = baseData?.endTransportingConditions.find(
    (c) => c.id === routeParams.endTransportingCondition
  );

  const isContainer = useMemo(
    () => routeParams.cargoType && ['container', 'auto'].indexOf(routeParams.cargoType) !== -1,
    [routeParams.cargoType]
  );

  const isWithoutPrekeridgeEnabled =
    startLocation?.type !== 'terminal' && startTransportingCondition?.id === '3';
  const isWithoutOnkeridgeEnabled =
    endLocation?.type !== 'terminal' && endTransportingCondition?.id === '3';
  const isWithCrossDockingEnabled = ['container', 'auto'].indexOf(routeParams.cargoType) !== -1;

  const ExpandIcon = isExpanded ? ExpandLessIcon : ExpandMoreIcon;

  const toggleAccordionExpandHandler = () => {
    setIsExpanded((prevState) => !prevState);
  };

  const changeRouteParamsHandler = <T extends keyof RouteWidgetParams>(
    key: T,
    value: RouteWidgetParams[T]
  ) => {
    setRouteParameter((prevState) => ({
      ...prevState,
      containerParameters: {
        ...prevState.containerParameters,
        containerParameters: prevState.containerParameters.containerParameters.map((c) => ({
          ...c,
          withPallets: key === 'isWithCrossDocking' && value === false ? false : c.withPallets,
          palletsLoaded: key === 'isWithCrossDocking' && value === false ? [] : c.palletsLoaded,
        })),
      },
      truckParameters: {
        ...prevState.truckParameters,
        truckParameters: prevState.truckParameters.truckParameters.map((t) => ({
          ...t,
          withPallets: key === 'isWithCrossDocking' && value === false ? false : t.withPallets,
          palletsLoaded: key === 'isWithCrossDocking' && value === false ? [] : t.palletsLoaded,
        })),
      },
      [key]: value,
    }));
  };

  return (
    <div className="base-params__wrap">
      <div>
        <MButton
          variant={'outlined'}
          color={'inherit'}
          style={{
            backgroundColor: checkIsAlternativeVariantsChanged(
              routeParams,
              DELIVERY_FORM_CONSTANTS.routeParams
            )
              ? '#E9FCD4'
              : 'inherit',
          }}
          fullWidth={true}
          endIcon={<ExpandIcon fontSize="medium" sx={{ color: '#383838' }} />}
          onClick={toggleAccordionExpandHandler}
        >
          {t('RoutesListComponent:AlternativeVariants.Title')}
        </MButton>
      </div>

      {isExpanded && (
        <div className="base-params__items">
          <FormControlLabel
            className="base-params__item"
            label={
              <>
                <VariantTooltip variantName="withPreCarriage" />
                <span>{t('RoutesListComponent:AlternativeVariants.Variants.withPreCarriage')}</span>
              </>
            }
            control={
              <Checkbox
                size={'medium'}
                checked={isNeedPrekeridge}
                disabled={
                  !startTransportingCondition.is_prekeridge_available ||
                  isRoutesLoading ||
                  isLoading
                }
                onChange={() => changeRouteParamsHandler('isNeedPrekeridge', !isNeedPrekeridge)}
              />
            }
          />
          <FormControlLabel
            className="base-params__item"
            label={
              <>
                <VariantTooltip variantName="soc" />
                <span>{t('RoutesListComponent:AlternativeVariants.Variants.soc')}</span>
              </>
            }
            control={
              <Checkbox
                size={'medium'}
                checked={!isNeedContainerRent}
                disabled={!isContainer || isRoutesLoading || isLoading}
                onChange={() =>
                  changeRouteParamsHandler('isNeedContainerRent', !isNeedContainerRent)
                }
              />
            }
          />
          <FormControlLabel
            className="base-params__item"
            label={
              <>
                <VariantTooltip variantName="withoutPreCarriage" />
                <span>
                  {t('RoutesListComponent:AlternativeVariants.Variants.withoutPreCarriage')}
                </span>
              </>
            }
            control={
              <Checkbox
                size={'medium'}
                checked={isWithoutPrekeridge}
                disabled={!isWithoutPrekeridgeEnabled || isRoutesLoading || isLoading}
                onChange={() =>
                  changeRouteParamsHandler('isWithoutPrekeridge', !isWithoutPrekeridge)
                }
              />
            }
          />
          <FormControlLabel
            className="base-params__item"
            label={
              <>
                <VariantTooltip variantName="withoutOnCarriage" />
                <span>
                  {t('RoutesListComponent:AlternativeVariants.Variants.withoutOnCarriage')}
                </span>
              </>
            }
            control={
              <Checkbox
                size={'medium'}
                checked={isWithoutOnkeridge}
                disabled={!isWithoutOnkeridgeEnabled || isRoutesLoading || isLoading}
                onChange={() => changeRouteParamsHandler('isWithoutOnkeridge', !isWithoutOnkeridge)}
              />
            }
          />
          <FormControlLabel
            className="base-params__item"
            label={
              <>
                <VariantTooltip variantName="fromTerminalRadius" />
                <span>
                  {t('RoutesListComponent:AlternativeVariants.Variants.fromTerminalRadius')}
                </span>
              </>
            }
            control={
              <Checkbox
                size={'medium'}
                checked={isFromTerminalRadius}
                disabled={isRoutesLoading || isLoading}
                onChange={() =>
                  changeRouteParamsHandler('isFromTerminalRadius', !isFromTerminalRadius)
                }
              />
            }
          />
          <FormControlLabel
            className="base-params__item"
            label={
              <>
                <VariantTooltip variantName="toTerminalRadius" />
                <span>
                  {t('RoutesListComponent:AlternativeVariants.Variants.toTerminalRadius')}
                </span>
              </>
            }
            control={
              <Checkbox
                size={'medium'}
                checked={isToTerminalRadius}
                disabled={isRoutesLoading || isLoading}
                onChange={() => changeRouteParamsHandler('isToTerminalRadius', !isToTerminalRadius)}
              />
            }
          />
          <FormControlLabel
            className="base-params__item"
            label={
              <>
                <VariantTooltip variantName="withCrossDocking" />
                <span>
                  {t('RoutesListComponent:AlternativeVariants.Variants.withCrossDocking')}
                </span>
              </>
            }
            control={
              <Checkbox
                size={'medium'}
                checked={isWithCrossDocking}
                disabled={isRoutesLoading || isLoading || !isWithCrossDockingEnabled}
                onChange={() => changeRouteParamsHandler('isWithCrossDocking', !isWithCrossDocking)}
              />
            }
          />
        </div>
      )}
    </div>
  );
};

// Экспортируем компонент
export default RouteParameters;
